import React, { useState } from 'react';
import { Stock } from '../StockComparison';
import { BarChart2 } from 'lucide-react';
interface ChartViewProps {
  stocks: Stock[];
}
export function ChartView({
  stocks
}: ChartViewProps) {
  const [selectedGroup, setSelectedGroup] = useState<string>('growth');
  if (stocks.length === 0) {
    return <div className="text-theme-secondary text-center py-8">
        No stocks selected to compare
      </div>;
  }
  // Define metric groups with colors
  const metricGroups = {
    growth: {
      label: 'Growth Metrics',
      metrics: [{
        id: 'ttm_revenue_growth',
        label: 'TTM Revenue Growth',
        color: 'bg-teal-500'
      }, {
        id: 'ttm_eps_growth',
        label: 'TTM EPS Growth',
        color: 'bg-pink-500'
      }]
    },
    margins: {
      label: 'Margin Metrics',
      metrics: [{
        id: 'ttm_gross_margin',
        label: 'TTM Gross Margin',
        color: 'bg-pink-500'
      }, {
        id: 'ttm_revenue_growth',
        label: 'TTM Revenue Growth',
        color: 'bg-teal-500'
      }]
    },
    valuation: {
      label: 'Valuation Metrics',
      metrics: [{
        id: 'ttm_pe',
        label: 'TTM P/E',
        color: 'bg-indigo-500'
      }, {
        id: 'forward_pe',
        label: 'Forward P/E',
        color: 'bg-cyan-500'
      }]
    }
  };
  // Get the maximum value for the current group's metrics to scale the chart
  const getMaxValue = () => {
    let max = 0;
    stocks.forEach(stock => {
      metricGroups[selectedGroup as keyof typeof metricGroups].metrics.forEach(metric => {
        const value = stock.metrics[metric.id] || 0;
        max = Math.max(max, value);
      });
    });
    // Ensure we have a non-zero max value to avoid division by zero
    return Math.max(max, 1);
  };
  const currentGroup = metricGroups[selectedGroup as keyof typeof metricGroups];
  const maxValue = getMaxValue();
  // Generate y-axis values
  const yAxisValues = [100, 80, 60, 40, 20, 0];
  // Debug function to check if metrics have values
  const hasValues = stocks.some(stock => currentGroup.metrics.some(metric => (stock.metrics[metric.id] || 0) > 0));
  return <div className="space-y-4">
      <div className="bg-theme-secondary rounded-lg p-6">
        {/* Header with Title and Group Selection */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-theme-primary">
            Financial Metrics Comparison
          </h3>
          <div className="flex space-x-2">
            {Object.entries(metricGroups).map(([groupId, group]) => <button key={groupId} onClick={() => setSelectedGroup(groupId)} className={`px-4 py-2 text-sm rounded-lg transition-colors ${selectedGroup === groupId ? 'bg-blue-600 text-theme-primary' : 'bg-theme-tertiary text-theme-secondary hover:bg-gray-600'}`}>
                {group.label}
              </button>)}
          </div>
        </div>
        {/* Debug info - will remove in final version */}
        <div className="mb-4 text-xs text-theme-muted">
          {hasValues ? `Displaying ${currentGroup.metrics.length} metrics for ${stocks.length} stocks. Max value: ${maxValue}` : 'Warning: No positive values found for the selected metrics.'}
        </div>
        {/* Chart Area with fixed height */}
        <div className="relative h-[400px] border border-theme-tertiary rounded-lg mb-4">
          {/* Y-axis grid lines and labels */}
          <div className="absolute inset-0 grid grid-cols-1 grid-rows-5 h-full">
            {yAxisValues.map((value, i) => <div key={i} className="relative border-t border-theme-tertiary/50">
                <span className="absolute -left-8 top-0 text-xs text-theme-muted">
                  {value}%
                </span>
              </div>)}
          </div>
          {/* Bars - simplified implementation */}
          <div className="absolute inset-x-8 inset-y-4 flex items-end">
            {stocks.map((stock, stockIndex) => <div key={stock.symbol} className="flex-1 flex justify-center h-full">
                <div className="w-4/5 h-full flex justify-around">
                  {currentGroup.metrics.map((metric, metricIndex) => {
                const value = stock.metrics[metric.id] || 0;
                // Calculate percentage with a minimum height to ensure visibility
                const percentage = Math.max(1, Math.min(100, value / maxValue * 100));
                return <div key={`${stock.symbol}-${metric.id}`} className="flex flex-col items-center justify-end h-full w-full max-w-[50px]">
                        <div className={`w-full ${metric.color} rounded-t border border-theme-tertiary relative group`} style={{
                    height: `${percentage}%`
                  }}>
                          <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-theme-primary text-xs bg-theme-secondary px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {value.toFixed(1)}%
                          </span>
                        </div>
                      </div>;
              })}
                </div>
              </div>)}
          </div>
        </div>
        {/* X-axis labels */}
        <div className="flex justify-around px-8 mb-4">
          {stocks.map(stock => <div key={stock.symbol} className={`text-sm font-medium ${stock.symbol === 'SPY' ? 'text-blue-400' : 'text-theme-primary'}`}>
              {stock.symbol}
            </div>)}
        </div>
        {/* Legend */}
        <div className="flex justify-center gap-8 border-t border-theme-tertiary pt-4">
          {currentGroup.metrics.map(metric => <div key={metric.id} className="flex items-center">
              <div className={`w-4 h-4 mr-2 rounded-sm ${metric.color}`}></div>
              <span className="text-sm text-theme-secondary">{metric.label}</span>
            </div>)}
        </div>
      </div>
    </div>;
}